/**
 * Per-role grants API — connectors, skills, permissions
 * (Milestone 1.5b, spec §3.5).
 *
 * Mirrors apps/chaos-gateway/src/role-grants.ts. Two prefixes:
 *   /api/orgs/:orgId/roles/:roleId/...     — org-scoped
 *   /api/global-roles/:roleId/...          — system-founder only
 *
 * All POST writes are idempotent.
 */
import type {
  CreateRoleConnectorGrantInput,
  CreateRolePermissionInput,
  CreateRoleSkillGrantInput,
  RoleConnectorGrant,
  RolePermission,
  RoleSkillGrant,
  RoleSkillGrantBoardScope,
} from '@chaos/types/registries';

import { apiJson } from './client';

// Enriched grant rows surfaced by the gateway (joined w/ registry).
export interface ConnectorGrantRow extends RoleConnectorGrant {
  connector: {
    id: string;
    slug: string;
    displayName: string;
    category: string | null;
    enabled: boolean;
  };
}

export interface SkillGrantRow extends RoleSkillGrant {
  skill: {
    id: string;
    slug: string;
    displayName: string;
    category: string | null;
    enabled: boolean;
  };
}

export interface PermissionGrantRow extends RolePermission {
  permissionMeta: {
    displayName: string;
    category: string | null;
    description: string | null;
  };
}

/**
 * A board a grant may be narrowed to (migration 0217).
 *
 * `orgName` is only meaningful for a GLOBAL role, whose picker spans
 * Workspaces; an org-scoped role's picker is already inside one.
 */
export interface SkillGrantBoard {
  id: string;
  slug: string;
  displayName: string;
  icon: string | null;
  projectId: string;
  projectName: string | null;
  orgId: string;
  orgName: string | null;
}

interface SkillGrantBoardsResponse {
  boards: SkillGrantBoard[];
}

interface ConnectorGrantsListResponse {
  grants: ConnectorGrantRow[];
}
interface SkillGrantsListResponse {
  grants: SkillGrantRow[];
}
interface PermissionGrantsListResponse {
  grants: PermissionGrantRow[];
}

// Org-scoped paths
function orgPrefix(orgId: string, roleId: string): string {
  return `/orgs/${orgId}/roles/${roleId}`;
}
function globalPrefix(roleId: string): string {
  return `/global-roles/${roleId}`;
}

export const roleGrantsApi = {
  // ── Org-scoped role grants ────────────────────────────────────────
  listConnectorGrants(orgId: string, roleId: string): Promise<ConnectorGrantRow[]> {
    return apiJson<ConnectorGrantsListResponse>(
      `${orgPrefix(orgId, roleId)}/connector-grants`,
    ).then((r) => r.grants);
  },
  grantConnector(
    orgId: string,
    roleId: string,
    input: CreateRoleConnectorGrantInput,
  ): Promise<RoleConnectorGrant> {
    return apiJson<{ grant: RoleConnectorGrant }>(
      `${orgPrefix(orgId, roleId)}/connector-grants`,
      { method: 'POST', json: input },
    ).then((r) => r.grant);
  },
  revokeConnector(orgId: string, roleId: string, connectorId: string): Promise<{ ok: boolean }> {
    return apiJson<{ ok: boolean }>(
      `${orgPrefix(orgId, roleId)}/connector-grants/${connectorId}`,
      { method: 'DELETE' },
    );
  },

  listSkillGrants(orgId: string, roleId: string): Promise<SkillGrantRow[]> {
    return apiJson<SkillGrantsListResponse>(`${orgPrefix(orgId, roleId)}/skill-grants`).then(
      (r) => r.grants,
    );
  },
  grantSkill(
    orgId: string,
    roleId: string,
    input: CreateRoleSkillGrantInput,
  ): Promise<RoleSkillGrant> {
    return apiJson<{ grant: RoleSkillGrant }>(`${orgPrefix(orgId, roleId)}/skill-grants`, {
      method: 'POST',
      json: input,
    }).then((r) => r.grant);
  },
  revokeSkill(orgId: string, roleId: string, skillId: string): Promise<{ ok: boolean }> {
    return apiJson<{ ok: boolean }>(`${orgPrefix(orgId, roleId)}/skill-grants/${skillId}`, {
      method: 'DELETE',
    });
  },

  // 0217 — the reach of one grant. `boardIds` is the WHOLE list and replaces
  // what is stored; it is sent only with 'selected', because a request that
  // says "every board" while naming boards has two readings and the server
  // refuses to guess which one the screen showed.
  listSkillGrantBoards(orgId: string): Promise<SkillGrantBoard[]> {
    return apiJson<SkillGrantBoardsResponse>(`/orgs/${orgId}/skill-grant-boards`).then(
      (r) => r.boards,
    );
  },
  setSkillGrantBoardScope(
    orgId: string,
    roleId: string,
    skillId: string,
    scope: RoleSkillGrantBoardScope,
    boardIds: string[],
  ): Promise<RoleSkillGrant> {
    return apiJson<{ grant: RoleSkillGrant }>(
      `${orgPrefix(orgId, roleId)}/skill-grants/${skillId}/board-scope`,
      {
        method: 'PUT',
        json: scope === 'selected' ? { scope, board_ids: boardIds } : { scope },
      },
    ).then((r) => r.grant);
  },

  listPermissionGrants(orgId: string, roleId: string): Promise<PermissionGrantRow[]> {
    return apiJson<PermissionGrantsListResponse>(`${orgPrefix(orgId, roleId)}/permissions`).then(
      (r) => r.grants,
    );
  },
  grantPermission(
    orgId: string,
    roleId: string,
    input: CreateRolePermissionInput,
  ): Promise<RolePermission> {
    return apiJson<{ grant: RolePermission }>(`${orgPrefix(orgId, roleId)}/permissions`, {
      method: 'POST',
      json: input,
    }).then((r) => r.grant);
  },
  revokePermission(
    orgId: string,
    roleId: string,
    permission: string,
  ): Promise<{ ok: boolean }> {
    return apiJson<{ ok: boolean }>(
      `${orgPrefix(orgId, roleId)}/permissions/${encodeURIComponent(permission)}`,
      { method: 'DELETE' },
    );
  },

  // ── Global-role grants (founder only) ─────────────────────────────
  listGlobalConnectorGrants(roleId: string): Promise<ConnectorGrantRow[]> {
    return apiJson<ConnectorGrantsListResponse>(`${globalPrefix(roleId)}/connector-grants`).then(
      (r) => r.grants,
    );
  },
  grantGlobalConnector(
    roleId: string,
    input: CreateRoleConnectorGrantInput,
  ): Promise<RoleConnectorGrant> {
    return apiJson<{ grant: RoleConnectorGrant }>(`${globalPrefix(roleId)}/connector-grants`, {
      method: 'POST',
      json: input,
    }).then((r) => r.grant);
  },
  revokeGlobalConnector(roleId: string, connectorId: string): Promise<{ ok: boolean }> {
    return apiJson<{ ok: boolean }>(`${globalPrefix(roleId)}/connector-grants/${connectorId}`, {
      method: 'DELETE',
    });
  },

  listGlobalSkillGrants(roleId: string): Promise<SkillGrantRow[]> {
    return apiJson<SkillGrantsListResponse>(`${globalPrefix(roleId)}/skill-grants`).then(
      (r) => r.grants,
    );
  },
  grantGlobalSkill(roleId: string, input: CreateRoleSkillGrantInput): Promise<RoleSkillGrant> {
    return apiJson<{ grant: RoleSkillGrant }>(`${globalPrefix(roleId)}/skill-grants`, {
      method: 'POST',
      json: input,
    }).then((r) => r.grant);
  },
  revokeGlobalSkill(roleId: string, skillId: string): Promise<{ ok: boolean }> {
    return apiJson<{ ok: boolean }>(`${globalPrefix(roleId)}/skill-grants/${skillId}`, {
      method: 'DELETE',
    });
  },

  // 0217 — a global role is held in every Workspace it is published into, so
  // its picker is the account-wide board list and each row names its Workspace.
  listGlobalSkillGrantBoards(roleId: string): Promise<SkillGrantBoard[]> {
    return apiJson<SkillGrantBoardsResponse>(`${globalPrefix(roleId)}/skill-grant-boards`).then(
      (r) => r.boards,
    );
  },
  setGlobalSkillGrantBoardScope(
    roleId: string,
    skillId: string,
    scope: RoleSkillGrantBoardScope,
    boardIds: string[],
  ): Promise<RoleSkillGrant> {
    return apiJson<{ grant: RoleSkillGrant }>(
      `${globalPrefix(roleId)}/skill-grants/${skillId}/board-scope`,
      {
        method: 'PUT',
        json: scope === 'selected' ? { scope, board_ids: boardIds } : { scope },
      },
    ).then((r) => r.grant);
  },

  listGlobalPermissionGrants(roleId: string): Promise<PermissionGrantRow[]> {
    return apiJson<PermissionGrantsListResponse>(`${globalPrefix(roleId)}/permissions`).then(
      (r) => r.grants,
    );
  },
  grantGlobalPermission(
    roleId: string,
    input: CreateRolePermissionInput,
  ): Promise<RolePermission> {
    return apiJson<{ grant: RolePermission }>(`${globalPrefix(roleId)}/permissions`, {
      method: 'POST',
      json: input,
    }).then((r) => r.grant);
  },
  revokeGlobalPermission(roleId: string, permission: string): Promise<{ ok: boolean }> {
    return apiJson<{ ok: boolean }>(
      `${globalPrefix(roleId)}/permissions/${encodeURIComponent(permission)}`,
      { method: 'DELETE' },
    );
  },
};

export const roleGrantsKeys = {
  all: ['role-grants'] as const,
  // org-scoped
  connectorGrants: (orgId: string, roleId: string) =>
    [...roleGrantsKeys.all, 'connector', orgId, roleId] as const,
  skillGrants: (orgId: string, roleId: string) =>
    [...roleGrantsKeys.all, 'skill', orgId, roleId] as const,
  permissionGrants: (orgId: string, roleId: string) =>
    [...roleGrantsKeys.all, 'permission', orgId, roleId] as const,
  // global
  globalConnectorGrants: (roleId: string) =>
    [...roleGrantsKeys.all, 'global-connector', roleId] as const,
  globalSkillGrants: (roleId: string) =>
    [...roleGrantsKeys.all, 'global-skill', roleId] as const,
  // 0217 — the board picker's list.
  skillGrantBoards: (orgId: string) =>
    [...roleGrantsKeys.all, 'skill-grant-boards', orgId] as const,
  globalSkillGrantBoards: (roleId: string) =>
    [...roleGrantsKeys.all, 'global-skill-grant-boards', roleId] as const,
  globalPermissionGrants: (roleId: string) =>
    [...roleGrantsKeys.all, 'global-permission', roleId] as const,
};
